<!--
 * Shared empty state for pages and panels.
 *
 * Two variants:
 * - default: full-height hero with the violet glow, a `text-3xl` title and an
 *   optional blurred preview of the list behind it (`#empty-state-item`).
 * - compact: `text-heading-2` / `text-sm` stack with no glow and no backdrop,
 *   sized for in-column and card contexts (settings bodies, page sections).
 *
 * `icon` renders the branded TolaSignal above the title in either variant; it
 * is off by default so existing consumers are unchanged.
 *
 * Attributes fall through to the root `<section>`, so layout tweaks such as
 * `class="h-full"` can be applied by the consumer.
-->
<script setup>
import { computed } from 'vue';

import Policy from 'dashboard/components/policy.vue';
import TolaSignal from 'dashboard/components-next/empty-state/TolaSignal.vue';

const props = defineProps({
  title: {
    type: String,
    required: true,
  },
  subtitle: {
    type: String,
    required: true,
  },
  actionPerms: {
    type: Array,
    default: () => [],
  },
  showBackdrop: {
    type: Boolean,
    default: true,
  },
  // Lucide icon rendered inside TolaSignal above the title, eg. i-lucide-antenna.
  icon: {
    type: String,
    default: '',
  },
  compact: {
    type: Boolean,
    default: false,
  },
});

const rootClass = computed(() =>
  props.compact
    ? 'flex flex-col items-center justify-center w-full px-6 py-16 text-center'
    : 'relative flex flex-col items-center justify-center w-full h-full overflow-hidden'
);
</script>

<template>
  <section :class="rootClass">
    <template v-if="compact">
      <TolaSignal v-if="icon" :icon="icon" />
      <h2 class="mt-4 mb-0 text-heading-2 text-n-slate-12">
        {{ title }}
      </h2>
      <p v-if="subtitle" class="max-w-md mt-2 mb-0 text-sm text-n-slate-11">
        {{ subtitle }}
      </p>
      <Policy v-if="$slots.actions" :permissions="actionPerms" class="mt-6">
        <slot name="actions" />
      </Policy>
    </template>
    <template v-else>
      <div
        aria-hidden="true"
        class="absolute inset-x-0 mx-auto rounded-full pointer-events-none top-1/4 size-[26rem] bg-n-brand-violet/20 dark:bg-n-brand-violet/30 blur-[110px]"
      />
      <div
        class="relative w-full max-w-5xl mx-auto overflow-hidden h-full max-h-[28rem]"
      >
        <div
          v-if="showBackdrop"
          class="w-full h-full space-y-4 overflow-y-hidden opacity-50 pointer-events-none"
        >
          <slot name="empty-state-item" />
        </div>
        <div
          class="flex flex-col items-center justify-end w-full h-full pb-20"
          :class="{
            'absolute inset-x-0 bottom-0 bg-gradient-to-t from-n-surface-1 from-25% to-transparent':
              showBackdrop,
          }"
        >
          <div
            class="flex flex-col items-center justify-center gap-6"
            :class="{
              'mt-48': !showBackdrop,
            }"
          >
            <div class="flex flex-col items-center justify-center gap-3">
              <TolaSignal v-if="icon" :icon="icon" />
              <h2 class="text-3xl font-medium text-center text-n-slate-12">
                {{ title }}
              </h2>
              <p
                v-if="subtitle"
                class="max-w-xl text-base text-center text-n-slate-11 tracking-[0.3px]"
              >
                {{ subtitle }}
              </p>
            </div>
            <Policy :permissions="actionPerms">
              <slot name="actions" />
            </Policy>
          </div>
        </div>
      </div>
    </template>
  </section>
</template>
